export interface TimeZoneOption {
  /** IANA identifier, e.g. "America/Denver". This is what gets stored. */
  id: string;
  /** Label shown to the user, e.g. "America/Denver (10:00 AM)". */
  label: string;
}

/** IANA zone of the browser. It does not change during a session, so it is resolved once. */
export const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export let clientTimeZone = browserTimeZone;

export const setClientTimeZone = (timeZone: string) => {
  clientTimeZone = timeZone || browserTimeZone;
};

const getCurrentTimeLabel = (timeZone: string, date: Date) => {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit', hour12: true }).format(
      date,
    );
  } catch {
    return '';
  }
};

export const toTimeZoneOption = (id: string, date: Date = new Date()): TimeZoneOption => {
  const time = getCurrentTimeLabel(id, date);
  return { id, label: time ? `${id} (${time})` : id };
};

const getSupportedTimeZoneIds = (): string[] => {
  if (typeof Intl.supportedValuesOf === 'function') {
    return Intl.supportedValuesOf('timeZone');
  }
  return [browserTimeZone];
};

/**
 * IANA time zones supported by the current browser, sorted by id, each labelled with the
 * current time in that zone. Not cached on purpose: the time in the label should be fresh
 * whenever the picker is mounted.
 */
export const getTimeZoneOptions = (date: Date = new Date()): TimeZoneOption[] =>
  getSupportedTimeZoneIds().map((id) => toTimeZoneOption(id, date));
